import { useCallback, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { isTerminal } from "../api.js";
import { SUPPORTED_LOCALES, localeDisplayName } from "../i18n/locales.js";
import { useChangeLocale, useLocale, useLocalePath } from "../i18n/hooks.js";
import { useDismiss } from "../lib/useDismiss.js";
import { useSession } from "../session.jsx";
import { useWorkspace } from "../workspace.jsx";
import { Icon, Logo } from "./Icon.jsx";
import { LanguageSwitcher } from "./LanguageSwitcher.jsx";
import { Credits, StatusPill } from "./ui.jsx";

function ViewSwitch({ enabled }) {
  const intl = useIntl();
  const { view, setView } = useWorkspace();
  const items = [
    { id: "graph", icon: "graph" },
    { id: "table", icon: "table" },
    { id: "map", icon: "map", disabled: true },
  ];
  return (
    <div className="seg" role="tablist" aria-label={intl.formatMessage({ id: "topbar.view" })}>
      {items.map((it) => {
        const disabled = !enabled || it.disabled;
        const title = it.disabled
          ? intl.formatMessage({ id: "topbar.mapUnavailable" })
          : !enabled
            ? intl.formatMessage({ id: "topbar.viewHint" })
            : undefined;
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={enabled && view === it.id}
            className={`seg-btn ${enabled && view === it.id ? "is-active" : ""}`}
            disabled={disabled}
            title={title}
            onClick={() => setView(it.id)}
          >
            <Icon name={it.icon} size={15} />
            <FormattedMessage id={`view.${it.id}`} />
          </button>
        );
      })}
    </div>
  );
}

function TasksMenu() {
  const intl = useIntl();
  const lp = useLocalePath();
  const { tasks, runningCount } = useWorkspace();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  return (
    <div className="popover-wrap" ref={ref}>
      <button
        type="button"
        className={`tb-chip ${runningCount ? "is-busy" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={intl.formatMessage({ id: "topbar.tasks" })}
      >
        {runningCount ? <span className="pulse-dot" aria-hidden="true" /> : <Icon name="tasks" size={16} />}
        <span className="tb-chip-num">{runningCount}</span>
        <span className="tb-chip-label">
          <FormattedMessage id="topbar.tasksLabel" values={{ count: runningCount }} />
        </span>
      </button>
      {open ? (
        <div className="popover popover-end tasks-pop">
          <div className="popover-title">
            <FormattedMessage id="topbar.tasks" />
          </div>
          {tasks.length === 0 ? (
            <p className="muted small pad">
              <FormattedMessage id="topbar.noTasks" />
            </p>
          ) : (
            <ul className="task-list">
              {tasks.map(({ job, investigation }) => (
                <li key={job.id}>
                  <Link to={lp(`/jobs/${job.id}`)} onClick={close} className="task-item">
                    <span className="task-name">{investigation?.name || job.id.slice(0, 8)}</span>
                    <span className="task-meta">
                      {!isTerminal(job.status) ? (
                        <FormattedMessage
                          id="topbar.taskStage"
                          values={{
                            n: (job.progress?.stage ?? 0) + 1,
                            total: job.progress?.stages?.length || 7,
                          }}
                        />
                      ) : null}
                      <StatusPill ns="jobStatus" value={job.status} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AvatarMenu() {
  const intl = useIntl();
  const lp = useLocalePath();
  const navigate = useNavigate();
  const locale = useLocale();
  const changeLocale = useChangeLocale();
  const { user, memberships, tenantId, selectTenant, logout } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const initials = (user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="popover-wrap" ref={ref}>
      <button
        type="button"
        className="avatar"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: "topbar.account" })}
      >
        {initials}
      </button>
      {open ? (
        <div className="popover popover-end avatar-pop">
          <div className="pop-user">
            <div className="avatar avatar-lg">{initials}</div>
            <div className="pop-user-text">
              <strong className="truncate">{user?.email}</strong>
              <span className="muted small">
                <FormattedMessage id={`role.${memberships.find((m) => m.tenantId === tenantId)?.role || "viewer"}`} />
              </span>
            </div>
          </div>

          {memberships.length > 1 ? (
            <div className="pop-section">
              <div className="pop-label">
                <FormattedMessage id="topbar.organisations" />
              </div>
              {memberships.map((m) => (
                <button
                  key={m.tenantId}
                  type="button"
                  className={`pop-item ${m.tenantId === tenantId ? "is-active" : ""}`}
                  onClick={async () => {
                    close();
                    if (m.tenantId !== tenantId) {
                      await selectTenant(m.tenantId);
                      navigate(lp("/"));
                    }
                  }}
                >
                  <span className="truncate">{m.tenantName}</span>
                  {m.tenantId === tenantId ? <Icon name="check" size={15} /> : null}
                </button>
              ))}
            </div>
          ) : null}

          <div className="pop-section">
            <div className="pop-label">
              <FormattedMessage id="topbar.language" />
            </div>
            {SUPPORTED_LOCALES.map((l) => (
              <button
                key={l.code}
                type="button"
                className={`pop-item ${l.code === locale ? "is-active" : ""}`}
                onClick={() => {
                  close();
                  if (l.code !== locale) changeLocale(l.code);
                }}
              >
                <span>{localeDisplayName(l.code)}</span>
                {l.code === locale ? <Icon name="check" size={15} /> : null}
              </button>
            ))}
          </div>

          <div className="pop-section">
            <Link className="pop-item" to={lp("/settings")} onClick={close}>
              <span>
                <FormattedMessage id="nav.settings" />
              </span>
              <Icon name="settings" size={15} />
            </Link>
            <button
              type="button"
              className="pop-item"
              onClick={async () => {
                close();
                await logout();
                navigate(lp("/login"));
              }}
            >
              <span>
                <FormattedMessage id="topbar.logout" />
              </span>
              <Icon name="logout" size={15} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TopBar() {
  const intl = useIntl();
  const lp = useLocalePath();
  const { tenant, membership } = useSession();
  const { search, setSearch } = useWorkspace();
  const onResults = Boolean(useMatch("/:locale/jobs/:jobId"));
  const onList = Boolean(useMatch("/:locale/investigations"));

  return (
    <header className="topbar">
      <Link to={lp("/")} className="brand">
        <Logo size={26} />
        <span className="brand-name">
          <FormattedMessage id="brand.name" />
        </span>
      </Link>
      {membership ? <span className="tb-tenant truncate">{membership.tenantName}</span> : null}

      <div className="tb-center">
        <ViewSwitch enabled={onResults} />
      </div>

      <div className="tb-end">
        <label className="tb-search">
          <Icon name="search" size={15} />
          <span className="visually-hidden">{intl.formatMessage({ id: "topbar.search" })}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({
              id: onResults ? "topbar.searchResults" : onList ? "topbar.searchInvestigations" : "topbar.search",
            })}
            disabled={!onResults && !onList}
          />
        </label>
        <TasksMenu />
        <Link
          to={lp("/settings#credits")}
          className="tb-chip tb-credits"
          title={intl.formatMessage({ id: "topbar.credits" })}
        >
          <span className="credit-dot" aria-hidden="true" />
          <span className="tb-chip-num">
            <Credits value={tenant?.creditBalance ?? null} />
          </span>
          <span className="tb-chip-label">
            <FormattedMessage id="topbar.creditsUnit" />
          </span>
        </Link>
        <LanguageSwitcher className="tb-lang" />
        <AvatarMenu />
      </div>
    </header>
  );
}
